import { clsx } from 'clsx';
import type { ReactNode } from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'signal';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

const variantClasses = {
  default: 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]',
  success: 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20',
  warning: 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20',
  danger: 'bg-red-500/10 text-red-500 border border-red-500/20',
  info: 'bg-blue-500/10 text-blue-500 border border-blue-500/20',
  signal: '',
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  className,
}: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full font-semibold',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {children}
    </span>
  );
}

interface SignalBadgeProps {
  signal: string;
  size?: 'sm' | 'md';
}

export function SignalBadge({ signal, size = 'sm' }: SignalBadgeProps) {
  const getSignalStyle = () => {
    switch (signal) {
      case 'STRONG_BUY':
        return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
      case 'BUY':
        return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'HOLD':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'SELL':
        return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      case 'STRONG_SELL':
        return 'bg-red-500/20 text-red-400 border border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const getSignalLabel = () => {
    // Convert STRONG_BUY to "Strong Buy", SELL to "Sell", etc.
    return signal
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded-full font-bold',
        getSignalStyle(),
        sizeClasses[size]
      )}
    >
      {getSignalLabel()}
    </span>
  );
}
